import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import getOutputFilesFromStep from 'analysis-tool/data/getOutputFilesFromStep';
import DashboardLayout, {BlockSpec} from 'analysis-tool/ui/components/DashboardLayout';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepHeaderRouter, {doesStepSupportHeader} from 'analysis-tool/ui/main-view/StepHeaderRouter';
import {assert, assertAndContinue} from 'Asserts';
import ScratchFilePaneContent from 'pipelines/files/ScratchFilePaneContent';
import ToggleExpandButton from 'ui-library/buttons/ToggleExpandButton';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import Pane from 'ui-library/pane/Pane';
import UIPlaceholder from 'ui-library/UIPlaceholder';
import * as logging from 'util/Logging';

const expandedFileIdState = makeUseGlobalClientState<string | null>(null);

// Keep in sync with FILE_LIMIT in benchling/pipelines/data/models/pipeline_step_data.py
const FILE_LIMIT = 25;

/**
 * @package
 */
export const DEFAULT_BLOCK_MIN_HEIGHT = 100;

/**
 * Some scratchFile previews like html look better if they are taller than the default height because the might be
 * showing interesting content, so we can adjust their default `minHeight` here.
 *
 * Typically, this should be adjusted for any preview that doesn't render the default chip in `ScratchFilePaneContent`.
 *
 * @package
 */
export const MIMETYPE_TO_BLOCK_MIN_HEIGHT: {[mimetype: string]: number | undefined} = {
  'text/html': 448,
};

/**
 * Large images need a height limit so they don't grow taller than their container and cause overflow.
 * This is in contrast to HTML scratchFiles, where we do want overflow.
 *
 * @package
 */
export const MIMETYPE_TO_BLOCK_MAX_HEIGHT: {[mimetype: string]: number | undefined} = {
  'image/jpeg': 448,
  'image/png': 448,
};

function getFileBlockMinHeight(scratchFile: GetAnalysisScratchFile): number {
  assert(scratchFile.externalFileId != null, 'Pipeline scratchFile has not been finalized');
  return (
    (scratchFile.mimeType != null ? MIMETYPE_TO_BLOCK_MIN_HEIGHT[scratchFile.mimeType] : null) ??
    DEFAULT_BLOCK_MIN_HEIGHT
  );
}

function getFileBlockMaxHeight(scratchFile: GetAnalysisScratchFile): number | undefined {
  assert(scratchFile.externalFileId != null, 'Pipeline scratchFile has not been finalized');
  return scratchFile.mimeType != null ? MIMETYPE_TO_BLOCK_MAX_HEIGHT[scratchFile.mimeType] : undefined;
}

/**
 * Render the given step's output Files in a dashboard view
 */
export default function FileDashboardView(
  props: MainViewComponentProps<AnalysisMainViewItemType.FILE_DASHBOARD>
): JSX.Element {
  const [expandedFileId, setExpandedFileId] = expandedFileIdState.useGlobalClientState(props.clientStateId);
  const scratchFiles = getOutputFilesFromStep(props.step);
  if (scratchFiles.length > FILE_LIMIT) {
    logging.warn(`Expected < ${FILE_LIMIT} scratchFiles to render`);
  }

  const filteredFiles = scratchFiles.filter((scratchFile) => scratchFile.externalFileId != null);
  assertAndContinue(
    scratchFiles.length === filteredFiles.length,
    'One or more pipeline scratchFiles has not been finalized'
  );

  const blockSpecs: Array<BlockSpec> = filteredFiles.map((scratchFile) => {
    // Pipeline scratchFiles have already been filtered so this assertion is just for Typescript's sake
    assert(scratchFile.externalFileId != null, 'Pipeline scratchFile has not been finalized');
    return {
      key: scratchFile.id,
      // TODO(BNCH-45878): Add box-shadow
      block: (
        <Pane
          title={scratchFile.filename}
          toolbar={
            <ToggleExpandButton
              isExpanded={scratchFile.id === expandedFileId}
              onChange={() => {
                setExpandedFileId(scratchFile.id === expandedFileId ? null : scratchFile.id);
              }}
            />
          }
        >
          <div className={css(styles.filePaneContent)}>
            <ScratchFilePaneContent scratchFile={scratchFile} />
          </div>
        </Pane>
      ),
      minHeight: getFileBlockMinHeight(scratchFile),
      maxHeight: getFileBlockMaxHeight(scratchFile),
    };
  });

  return (
    <div className={css(styles.stepWrapper)}>
      {doesStepSupportHeader(props.step) && expandedFileId == null && (
        <div className={css(styles.stepHeader)}>
          <StepHeaderRouter {...props} />
        </div>
      )}
      {filteredFiles.length > 0 ? (
        <DashboardLayout blockSpecs={blockSpecs} expandedBlockKey={expandedFileId} />
      ) : (
        <UIPlaceholder infoSection="This step doesn't have any scratchFiles." />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  stepHeader: {
    // DashboardLayout has top padding. Undo it to make sure we don't have double spacing between the step
    // header and DashboardLayout.
    marginBottom: -ANALYSIS_TOOL_SPACING,
  },
  stepWrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  filePaneContent: {
    padding: ANALYSIS_TOOL_SPACING,
    height: '100%',
  },
});
